import { useState, useEffect, useCallback } from 'react';
import {
  TaskDetail,
  TaskFeedEntry,
  CreateFeedEntryInput,
  UpdateTaskInput,
  FeedType,
} from '../types/interfaces/task.types';
import { Severity } from '../types';
import * as taskService from '../services/taskService';
import useSnackbar from './useSnackbar';

interface UseTaskDetailReturn {
  task: TaskDetail | null;
  loading: boolean;
  error: string | null;
  fetchTaskDetail: (taskId: string) => Promise<void>;
  updateTask: (input: UpdateTaskInput) => Promise<boolean>;
  addComment: (message: string, type: FeedType) => Promise<boolean>;
  addAttachment: (url: string, name: string) => Promise<boolean>;
  refreshTask: () => Promise<void>;
}

export const useTaskDetail = (taskId?: string): UseTaskDetailReturn => {
  const [task, setTask] = useState<TaskDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const showSnackbar = useSnackbar();

  const fetchTaskDetail = useCallback(
    async (id: string) => {
      setLoading(true);
      setError(null);

      try {
        const taskData = await taskService.getTaskById(id, true) as TaskDetail;
        setTask(taskData);
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Failed to fetch task details';
        setError(errorMessage);
        showSnackbar(errorMessage, Severity.error);
      } finally {
        setLoading(false);
      }
    },
    [showSnackbar]
  );

  const updateTask = useCallback(
    async (input: UpdateTaskInput): Promise<boolean> => {
      if (!task) return false;

      setLoading(true);
      setError(null);

      try {
        const updatedTask = await taskService.updateTask(task.id, input);
        
        // Merge updated task with current feed
        setTask((prev) => prev ? { ...updatedTask, feed: prev.feed } as TaskDetail : null);
        
        showSnackbar('Task updated successfully', Severity.success);
        return true;
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Failed to update task';
        setError(errorMessage);
        showSnackbar(errorMessage, Severity.error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [task, showSnackbar]
  );

  const addComment = useCallback(
    async (message: string, type: FeedType): Promise<boolean> => {
      if (!task) return false;

      setLoading(true);
      setError(null);

      try {
        const input: CreateFeedEntryInput = {
          type,
          message,
        };

        const newEntry = await taskService.addFeedEntry(task.id, input);
        
        // Add new entry to feed
        setTask((prev) => 
          prev ? { ...prev, feed: [...prev.feed, newEntry] } : null
        );
        
        showSnackbar('Comment added successfully', Severity.success);
        return true;
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Failed to add comment';
        setError(errorMessage);
        showSnackbar(errorMessage, Severity.error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [task, showSnackbar]
  );

  const addAttachment = useCallback(
    async (url: string, name: string): Promise<boolean> => {
      if (!task) return false;

      setLoading(true);
      setError(null);

      try {
        const input: CreateFeedEntryInput = {
          type: 'ATTACHMENT' as any,
          attachmentUrl: url,
          attachmentName: name,
        };

        const newEntry = await taskService.addFeedEntry(task.id, input);
        
        // Add new entry to feed
        setTask((prev) => 
          prev ? { ...prev, feed: [...prev.feed, newEntry] } : null
        );
        
        showSnackbar('Attachment added successfully', Severity.success);
        return true;
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Failed to add attachment';
        setError(errorMessage);
        showSnackbar(errorMessage, Severity.error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [task, showSnackbar]
  );

  const refreshTask = useCallback(async () => {
    if (task) {
      await fetchTaskDetail(task.id);
    }
  }, [task, fetchTaskDetail]);

  // Initial fetch
  useEffect(() => {
    if (taskId) {
      fetchTaskDetail(taskId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  return {
    task,
    loading,
    error,
    fetchTaskDetail,
    updateTask,
    addComment,
    addAttachment,
    refreshTask,
  };
};

export default useTaskDetail;
